import { useEffect, useState } from 'react';
import { ArrowBackOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { FeedbackSnackbar, InformationPage, ResourceState, StatusBadge, useFeedbackSnackbar } from '../../../design-system';
import { integrationSettingsFixture } from '../fixtures/integrationSettingsFixtures';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { IntegrationSettingsRoute, ProfileMappingConfiguration, ProfileMappingProvider, ProviderProfileMappings } from '../model/types';
import type { IntegrationSettingsPort } from '../port/IntegrationSettingsPort';
import { IntegrationNavigationList } from './IntegrationNavigationList';
import { ProfileMappingDetail } from './ProfileMappingDetail';
import { GoogleWorkspaceMark, SlackMark } from './ProviderMarks';

export function DirectorySyncSettings({ port, fixtureMode, canWrite, readOnly, route, onNavigate }: {
  port: IntegrationSettingsPort;
  fixtureMode: boolean;
  canWrite: boolean;
  readOnly: boolean;
  route: 'directory' | 'google-workspace' | 'slack';
  onNavigate: (route: IntegrationSettingsRoute) => void;
}) {
  const [configuration, setConfiguration] = useState<ProfileMappingConfiguration>();
  const [updatingProvider, setUpdatingProvider] = useState<ProfileMappingProvider>();
  const [notice, setNotice] = useState<{ severity: 'error'; message: string }>();
  const { notify, dismiss, feedbackProps } = useFeedbackSnackbar();
  const providerKey = route === 'google-workspace' ? 'GOOGLE_WORKSPACE' : route === 'slack' ? 'SLACK' : undefined;
  const resource = useAsyncResource<ProfileMappingConfiguration>(
    () => fixtureMode ? Promise.resolve(integrationSettingsFixture) : port.getProfileMappingConfiguration(),
    [fixtureMode, port],
  );
  useEffect(() => {
    if (resource.data) setConfiguration(resource.data);
  }, [resource.data]);

  const navigate = (next: IntegrationSettingsRoute) => {
    setNotice(undefined);
    dismiss();
    onNavigate(next);
  };
  const updateProvider = (updated: ProviderProfileMappings) => {
    setConfiguration((current) => current ? {
      ...current, providers: current.providers.map((candidate) => candidate.provider === updated.provider ? updated : candidate),
    } : current);
  };
  const updateEnabled = async (provider: ProviderProfileMappings, enabled: boolean) => {
    if (!canWrite || readOnly || updatingProvider) return;
    setUpdatingProvider(provider.provider);
    setNotice(undefined);
    try {
      const updated = fixtureMode
        ? { ...provider, enabled, version: provider.version + 1, updatedAt: new Date().toISOString() }
        : await port.updateProviderEnabled(provider.provider, { enabled, version: provider.version });
      updateProvider(updated);
      notify(`${updated.displayName} 동기화를 ${updated.enabled ? '켰습니다' : '껐습니다'}. 다음 실행부터 적용됩니다.`, { tone: 'success' });
    } catch (reason) {
      if (reason instanceof Error && 'status' in reason && reason.status === 409) {
        try {
          const latest = await port.getProfileMappingConfiguration();
          setConfiguration(latest);
          setNotice({ severity: 'error', message: '다른 관리자가 설정을 변경해 최신 설정을 불러왔습니다. 동기화 사용 여부를 확인한 뒤 다시 변경해 주세요.' });
        } catch {
          setNotice({ severity: 'error', message: '설정이 변경되었지만 최신 설정을 불러오지 못했습니다. 잠시 후 다시 변경해 주세요.' });
        }
        return;
      }
      setNotice({
        severity: 'error',
        message: reason instanceof Error ? reason.message : '연동 상태를 변경하지 못했습니다.',
      });
    } finally {
      setUpdatingProvider(undefined);
    }
  };

  const provider = configuration?.providers.find((candidate) => candidate.provider === providerKey);
  if (provider) return <><ProfileMappingDetail
    key={provider.provider}
    provider={provider}
    sources={configuration?.sourceProperties ?? []}
    port={port}
    fixtureMode={fixtureMode}
    canWrite={canWrite}
    readOnly={readOnly}
    updatingEnabled={updatingProvider === provider.provider}
    enabledNotice={notice}
    onSuccess={(message) => notify(message, { tone: 'success' })}
    onEnabledChange={(enabled) => { void updateEnabled(provider, enabled); }}
    onSaved={updateProvider}
    onBack={() => navigate('directory')}
  /><FeedbackSnackbar {...feedbackProps} /></>;
  const directory = route === 'directory';
  return <InformationPage
    title={directory ? '디렉터리 동기화' : route === 'google-workspace' ? 'Google Workspace' : 'Slack'}
    width="compact"
    navigation={<IconButton
      aria-label={directory ? '연동 목록으로 돌아가기' : '디렉터리 동기화로 돌아가기'}
      onClick={() => navigate(directory ? 'overview' : 'directory')}
    ><ArrowBackOutlined /></IconButton>}
  >
    <ResourceState
      loading={resource.loading}
      error={resource.error?.message}
      empty={!resource.loading && (directory ? !configuration?.providers.length : !provider)}
      onRetry={resource.reload}
      loadingLabel="디렉터리 동기화 설정을 불러오는 중입니다."
      emptyTitle="연동 설정을 찾을 수 없습니다."
      emptyDescription="연동 제공자 설정을 확인한 뒤 다시 시도해 주세요."
    >
      {directory && configuration && <IntegrationNavigationList label="디렉터리 동기화 서비스" items={configuration.providers.map((candidate) => ({
        label: candidate.displayName,
        description: candidate.provider === 'GOOGLE_WORKSPACE'
          ? '프로필과 사진, 재직 상태, 조직 그룹을 동기화합니다.'
          : '기존 사용자의 이름·직책·조직 등 프로필 정보를 동기화합니다.',
        icon: candidate.provider === 'GOOGLE_WORKSPACE' ? <GoogleWorkspaceMark size={24} /> : <SlackMark size={24} />,
        value: <StatusBadge label={candidate.enabled ? '동기화 사용' : '동기화 중지'} tone={candidate.enabled ? 'success' : 'neutral'} />,
        ariaLabel: `${candidate.displayName} 상세 설정 열기`,
        onOpen: () => navigate(candidate.provider === 'GOOGLE_WORKSPACE' ? 'google-workspace' : 'slack'),
      }))} />}
    </ResourceState>
  </InformationPage>;
}
